// The Trace tab's Map view: the case as a mind map (markmap), drawn from the same steps as the timeline.
// markmap and d3 load only when someone opens the map. Drag to pan, scroll to zoom, click a node to fold it.
import React, { useEffect, useRef, useState } from 'react';
import { mapOf } from './timeline';

// production in slate, the new version in indigo; red only where the new version went wrong, and the path to it
const BRANCH = ['#64748B', '#4338CA'], RED = '#E02424';
// the critical point (the first place the new version went wrong) gets the 斬! strike burst with why; any other defect
// bold red text and a ✗ tag; the first difference, when it's somewhere else, a quiet ≠ marker
const burst = (note, calm) => ` <span class="ef-mburst${calm ? ' calm' : ''}"><b>${calm ? '≠' : '⚔ 斬!'}</b> ${note}</span>`;
const marked = (n) => {
  const p = n.payload || {};
  const text = p.defect ? `<span class="ef-mdiff">${n.content}</span>` : n.content;
  return { ...n, children: (n.children || []).map(marked),
    content: text + (p.critical ? burst(p.defect) : p.defect ? ` <span class="ef-mtag">✗ ${p.defect}</span>` : '')
      + (p.burst && !p.critical ? burst(`first difference: ${p.burst}`, true) : '') };
};

export default function TraceMap({ query, messages, sides, rows, fmt, first, defects, reg, onPick }) {
  const svg = useRef(null), mm = useRef(null), box = useRef(null);
  const [err, setErr] = useState(null);
  const [full, setFull] = useState(false);
  const data = marked(mapOf({ query, messages, sides, rows, fmt, first, defects, reg }));
  const bad = defects?.size > 0;
  const key = JSON.stringify(data);
  useEffect(() => {
    let stop = false;
    import('markmap-view').then(({ Markmap }) => {
      if (stop || !svg.current) return;
      const opts = { duration: 300, maxWidth: 300, spacingVertical: 8, spacingHorizontal: 90, initialExpandLevel: -1, paddingX: 10,
        color: (n) => (n.payload?.defect || n.payload?.inDefect || n.payload?.path ? RED : n.payload?.side != null ? BRANCH[n.payload.side] : '#4338CA') };
      if (!mm.current) mm.current = Markmap.create(svg.current, opts, data);
      else mm.current.setData(data, opts).then(() => mm.current.fit());
    }).catch(setErr);
    return () => { stop = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { mm.current?.destroy(); mm.current = null; }, []);
  useEffect(() => {                                   // full screen: the browser's own (Esc leaves it); refit either way
    const on = () => { setFull(document.fullscreenElement === box.current); setTimeout(() => mm.current?.fit(), 120); };
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (box.current?.requestFullscreen) box.current.requestFullscreen().catch(() => {});
    else { setFull(!full); setTimeout(() => mm.current?.fit(), 120); }   // no Fullscreen API (e.g. iPhone): fill the window
  };
  if (err) return <div className="xs dn">The map could not load: {String(err.message || err)}</div>;
  return <div className={'ef-map' + (full ? ' full' : '')} ref={box}>
    <svg ref={svg} onClick={(e) => {                  // the circle folds a node (markmap); the text opens its data
      if (e.target.closest('circle')) return;
      const ref = e.target.closest('g.markmap-node')?.__data__?.payload?.ref;
      if (!ref || !onPick) return;
      if (document.fullscreenElement) document.exitFullscreen();   // the data opens under the map
      else if (full) setFull(false);
      onPick(ref);
    }} />
    <div className="ef-map-bar xs fnt">Drag to move · scroll to zoom · click a node for its data, its circle to fold it ·
      {bad ? <> <span className="ef-mburst sm"><b>⚔ 斬!</b></span> = where it first went wrong · <span className="dn"><b>red ✗</b></span> = also wrong</> : ' nothing went wrong'}
      {' '}· ≠ = differs from production{first && <> · <span className="ef-mburst sm calm"><b>≠</b></span> = first difference</>}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        <button type="button" className="ef-btn sm ghost" onClick={() => mm.current?.fit()}>Fit</button>
        <button type="button" className="ef-btn sm ghost" onClick={toggleFull} title={full ? 'Exit full screen (Esc)' : 'Full screen'}>{full ? '✕ Exit full screen' : '⛶ Full screen'}</button></span></div>
  </div>;
}
